import Button from '@material-ui/core/Button';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCartTwoTone';
import React from 'react';
import { ProductQuantityLimits } from '../../@sprinx/knihovka-types/products';
import QuantityField, { QuantityFieldProps } from '../QuantityField';
import { ProductStockQuantityProduct, useProductStockQuantity } from '../../api/products/productStockInfo';
import { monitorStockAvailability } from '../../config';

export interface AddToCartButtonToQtyProps extends ProductQuantityLimits {
  classess?: any;
  onAddToCart: (nextQuantity: number) => Promise<any>;
  product: ProductStockQuantityProduct;
  quantity?: number;
  QuantityFieldProps?: Partial<QuantityFieldProps>;
  refButton?: React.Ref<any>;
  refInput?: React.Ref<any>;
}

export type AddToCartButtonToQtyClassKey = 'root' | 'buttonPadding' | 'addToCart';

const themeSettings = { name: 'AddToCartButtonToQty' };
const useStyles = makeStyles(
  createStyles<AddToCartButtonToQtyClassKey, {}>({
    root: {},
    buttonPadding: {
      paddingLeft: '15px',
    },
    addToCart: {
      backgroundColor: '#002969',
      color: 'white',
      '&:hover': {
        backgroundColor: '#003485',
      },
      '& span': {
        margin: 0,
      },
    },
  }),
  themeSettings,
);

const AddToCartButtonToQty: React.FC<AddToCartButtonToQtyProps> = ({
  onAddToCart,
  product,
  quantity,
  quantityDefault,
  quantityMax,
  quantityMin,
  quantityStep,
  quantityStepRounding,
  QuantityFieldProps: pQuantityFieldProps,
  refButton,
  refInput,
  classess,
}) => {
  const classes = useStyles({ classess });
  const [isDisabled, setDisabled] = React.useState(false);
  const [addQuantity, setAddQuantity] = React.useState(quantityDefault);
  const [onStockQty] = useProductStockQuantity(product);

  const unAvailableDueToMinQuantity = React.useMemo(() => {
    return monitorStockAvailability ? (product.quantityStep || 1) > onStockQty : false;
  }, [onStockQty, product.quantityStep]);

  const handleButtonClick = React.useCallback(async () => {
    setDisabled(true);
    await onAddToCart(addQuantity);
    setDisabled(false);
    setAddQuantity(quantityMin);
  }, [onAddToCart, addQuantity, quantityMin]);

  const handleQuantityChange = (current: number) => {
    if (current >= quantityMin) {
      setAddQuantity(current);
    }
  };

  const handleAddToCart = (current: number) => {
    if ((current || 0) >= quantityMin) {
      onAddToCart(current);
    }
  };

  const handleKeyDown = (event: any) => {
    if (event && event.key === 'Enter') {
      // target.value == onchage, addQuantity == +-
      const current = event.target && event.target.value ? event.target.value : addQuantity;

      if (current >= quantityMin) {
        event.preventDefault();
        onAddToCart(current);
      }
    }
  };

  if (monitorStockAvailability && onStockQty <= 0) {
    return null;
  }

  return (quantity || 0) > 0 ? (
    <QuantityField
      ref={refInput}
      disabled={unAvailableDueToMinQuantity}
      quantity={quantity}
      onChange={handleAddToCart}
      quantityMax={quantityMax}
      quantityMin={quantityMin}
      quantityStep={quantityStep}
      quantityStepRounding={quantityStepRounding}
      fullWidth
      {...pQuantityFieldProps}
    />
  ) : (
    <>
      <div>
        <QuantityField
          disabled={isDisabled || unAvailableDueToMinQuantity}
          ref={refInput}
          quantity={addQuantity}
          onChange={handleQuantityChange}
          quantityMax={quantityMax}
          quantityMin={quantityMin}
          quantityStep={quantityStep}
          quantityStepRounding={quantityStepRounding}
          onKeyDown={handleKeyDown}
          fullWidth
          {...pQuantityFieldProps}
        />
      </div>
      <div className={classes.buttonPadding}>
        <ButtonAddToCart
          className={classes}
          ref={refButton}
          disabled={isDisabled || unAvailableDueToMinQuantity}
          onClick={handleButtonClick}
        />
      </div>
    </>
  );
};

AddToCartButtonToQty.displayName = 'AddToCartButtonToQty';

export default AddToCartButtonToQty;

interface ButtonAddToCart {
  className: any;
  disabled?: boolean;
  onClick?: React.EventHandler<React.MouseEvent>;
}

const ButtonAddToCart = React.forwardRef<HTMLButtonElement, ButtonAddToCart>(
  ({ className, disabled, onClick }, ref) => {
    const classes = useStyles({ className });

    return (
      <Button
        ref={ref}
        className={classes.addToCart}
        variant='contained'
        disabled={disabled}
        onClick={onClick}
        startIcon={<AddShoppingCartIcon />}
      />
    );
  },
);

ButtonAddToCart.displayName = 'ButtonAddToCart';
